import React from 'react';

export interface ComposerWindowRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const STORAGE_KEY = 'cmail-composer-window';

// Floating-window sizing constants (px).
const DEFAULT_WIDTH = 640; // ~40rem — the previous default composer width
const DEFAULT_HEIGHT_RATIO = 0.6; // ~60vh — the previous default composer height
const MIN_WIDTH = 352; // ~22rem
const MIN_HEIGHT = 256; // ~16rem
const EDGE_MARGIN = 24; // keep the window this far inside the viewport edges

function getViewport(): { vw: number; vh: number } {
  return { vw: window.innerWidth, vh: window.innerHeight };
}

export function centeredDefaultRect(
  vw: number,
  vh: number
): ComposerWindowRect {
  const width = Math.min(DEFAULT_WIDTH, vw - EDGE_MARGIN * 2);
  const height = Math.min(
    Math.round(vh * DEFAULT_HEIGHT_RATIO),
    vh - EDGE_MARGIN * 2
  );
  return {
    width,
    height,
    left: Math.max(EDGE_MARGIN, Math.round((vw - width) / 2)),
    top: Math.max(EDGE_MARGIN, Math.round((vh - height) / 2)),
  };
}

// Clamp size to [min, viewport] and position so the window stays fully on-screen.
export function clampRect(
  rect: ComposerWindowRect,
  vw: number,
  vh: number
): ComposerWindowRect {
  const maxWidth = Math.max(MIN_WIDTH, vw - EDGE_MARGIN * 2);
  const maxHeight = Math.max(MIN_HEIGHT, vh - EDGE_MARGIN * 2);
  const width = Math.min(Math.max(rect.width, MIN_WIDTH), maxWidth);
  const height = Math.min(Math.max(rect.height, MIN_HEIGHT), maxHeight);
  const left = Math.min(
    Math.max(rect.left, EDGE_MARGIN),
    Math.max(EDGE_MARGIN, vw - width - EDGE_MARGIN)
  );
  const top = Math.min(
    Math.max(rect.top, EDGE_MARGIN),
    Math.max(EDGE_MARGIN, vh - height - EDGE_MARGIN)
  );
  return { left, top, width, height };
}

export function parseStoredRect(raw: string | null): ComposerWindowRect | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const keys = ['left', 'top', 'width', 'height'] as const;
    const valid = keys.every(
      (k) => typeof parsed?.[k] === 'number' && Number.isFinite(parsed[k])
    );
    if (!valid) return null;
    return {
      left: parsed.left,
      top: parsed.top,
      width: parsed.width,
      height: parsed.height,
    };
  } catch {
    return null;
  }
}

function loadInitialRect(vw: number, vh: number): ComposerWindowRect {
  let stored: ComposerWindowRect | null = null;
  try {
    stored = parseStoredRect(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    stored = null;
  }
  return clampRect(stored ?? centeredDefaultRect(vw, vh), vw, vh);
}

function persistRect(rect: ComposerWindowRect): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rect));
  } catch {
    // localStorage can be unavailable (private mode / quota) — non-fatal.
  }
}

interface UseComposerWindowArgs {
  readonly open: boolean;
  readonly isExpanded: boolean;
  readonly isMinimized: boolean;
}

interface UseComposerWindowResult {
  readonly windowRef: React.RefObject<HTMLDivElement>;
  readonly startDrag: (event: React.PointerEvent<HTMLElement>) => void;
  readonly isDragging: boolean;
}

/**
 * Manages the floating cmail composer window: position + size (persisted to
 * localStorage), header drag-to-move, and viewport clamping. Geometry is applied
 * imperatively (never via a React-controlled `style` prop) so native CSS
 * `resize` and live drag writes are not clobbered on re-render. In maximized /
 * minimized mode the inline geometry is cleared so the CSS class governs.
 */
export function useComposerWindow({
  open,
  isExpanded,
  isMinimized,
}: UseComposerWindowArgs): UseComposerWindowResult {
  const windowRef = React.useRef<HTMLDivElement>(null);
  const rectRef = React.useRef<ComposerWindowRect | null>(null);
  const dragCleanupRef = React.useRef<(() => void) | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const floating = open && !isExpanded && !isMinimized;

  const ensureRect = React.useCallback((): ComposerWindowRect => {
    const { vw, vh } = getViewport();
    rectRef.current = rectRef.current
      ? clampRect(rectRef.current, vw, vh)
      : loadInitialRect(vw, vh);
    return rectRef.current;
  }, []);

  const applyRectToDom = React.useCallback((rect: ComposerWindowRect) => {
    const el = windowRef.current;
    if (!el) return;
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
  }, []);

  const clearRectFromDom = React.useCallback(() => {
    const el = windowRef.current;
    if (!el) return;
    el.style.left = '';
    el.style.top = '';
    el.style.width = '';
    el.style.height = '';
  }, []);

  // Apply geometry on entering floating mode; clear it in maximized/minimized so
  // the CSS class wins. Intentionally not keyed on rect so live drag/resize
  // writes survive re-renders.
  React.useLayoutEffect(() => {
    if (floating) {
      applyRectToDom(ensureRect());
    } else {
      clearRectFromDom();
    }
  }, [floating, applyRectToDom, clearRectFromDom, ensureRect]);

  // Persist the result of native CSS resize.
  React.useEffect(() => {
    if (!floating) return undefined;
    const el = windowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const current = rectRef.current;
        if (!current) return;
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        if (width === current.width && height === current.height) return;
        // Growing (native resize, or the AI-panel min-width bump) can push the
        // right/bottom edge past the viewport — pull left/top back on-screen.
        const { vw, vh } = getViewport();
        const left = Math.min(
          current.left,
          Math.max(EDGE_MARGIN, vw - width - EDGE_MARGIN)
        );
        const top = Math.min(
          current.top,
          Math.max(EDGE_MARGIN, vh - height - EDGE_MARGIN)
        );
        rectRef.current = { left, top, width, height };
        if (left !== current.left) el.style.left = `${left}px`;
        if (top !== current.top) el.style.top = `${top}px`;
        persistRect(rectRef.current);
      });
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [floating]);

  // Keep the window on-screen when the viewport shrinks.
  React.useEffect(() => {
    if (!floating) return undefined;
    const onResize = () => {
      const { vw, vh } = getViewport();
      const clamped = clampRect(ensureRect(), vw, vh);
      rectRef.current = clamped;
      applyRectToDom(clamped);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [floating, applyRectToDom, ensureRect]);

  const startDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!floating || event.button !== 0) return;
      // Don't start a drag from interactive header controls.
      const target = event.target as HTMLElement;
      if (target.closest('button, a, input, [role="button"]')) return;

      const el = windowRef.current;
      if (!el) return;
      const rect = ensureRect();
      event.preventDefault();

      const startX = event.clientX;
      const startY = event.clientY;
      const originLeft = rect.left;
      const originTop = rect.top;
      let nextLeft = originLeft;
      let nextTop = originTop;
      let frame = 0;
      setIsDragging(true);
      document.body.style.userSelect = 'none';

      const onMove = (e: PointerEvent) => {
        const { vw, vh } = getViewport();
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        const left = originLeft + (e.clientX - startX);
        const top = originTop + (e.clientY - startY);
        nextLeft = Math.min(
          Math.max(left, EDGE_MARGIN),
          Math.max(EDGE_MARGIN, vw - width - EDGE_MARGIN)
        );
        nextTop = Math.min(
          Math.max(top, EDGE_MARGIN),
          Math.max(EDGE_MARGIN, vh - height - EDGE_MARGIN)
        );
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          el.style.left = `${nextLeft}px`;
          el.style.top = `${nextTop}px`;
        });
      };

      const teardown = () => {
        cancelAnimationFrame(frame);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        document.body.style.userSelect = '';
        dragCleanupRef.current = null;
      };

      const onUp = () => {
        teardown();
        rectRef.current = {
          left: nextLeft,
          top: nextTop,
          width: el.offsetWidth,
          height: el.offsetHeight,
        };
        persistRect(rectRef.current);
        setIsDragging(false);
      };

      dragCleanupRef.current = teardown;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    },
    [floating, ensureRect]
  );

  // Tear down an in-progress drag if the component unmounts mid-drag.
  React.useEffect(() => () => dragCleanupRef.current?.(), []);

  return { windowRef, startDrag, isDragging };
}
